
import Vue from 'vue';

const state = {
  // Loading
  appIsReady: false,
  initialDataLoaded: false,
  screenOverlayLogout: false,
  // State
  displayItemView: false,
  expandedCollections: [],
  highlightDragOverCollection: null,
  movingCollection: null,
  navigationHistory: [],
  selectedItems: [],
  selectedItemsObject: {
    id: null,
    notes: [],
    cards: [],
    items: [],
  },
  openCards: [],
  openNotes: [],
  openItems: [],
  displayAddedItemInBackgroundIndicator: false,
  // Quick lists, smart lists
  lastStarList: null,
  lastKeywordList: null,
  // Other / settings / search
  localSettings: {
    sidebarWidth: localStorage.getItem('sidebarWidth') || '16vw',
    collectionViewSortSetting: localStorage.getItem('collectionViewSortSetting') || 'stars-asc',
    noteViewSortSetting: localStorage.getItem('noteViewSortSetting') || 'stars',
  },
  search: {
    isActive: false,
    displayResults: false, // search can be active but not displayed, e.g. when we just navigated to a search result
  },
  expandedCollectionsInCollectionsList: [],
  scrollState: {
    collectionView: {},
    collectionNotes: {},
    itemNotes: {},
    cardDescription: {},
  },
  // Introduction
  introStep: 0, // off for the moment
};

const getters = {
  getSelectedItems: (state, getters, rootState) => {
    const lookFor = state.search.isActive ? 'search' : rootState.collections.selectedCollection;
    const found = state.selectedItems.find(item => lookFor === item.id);
    if (found) return found;
    return Object.assign({}, state.selectedItemsObject);
  },
};

const actions = {
  setSearchState({ dispatch, commit }, { isActive, displayResults }) {
    if (isActive !== undefined && isActive) commit('ActivateSearch');
    else if (isActive !== undefined && !isActive) {
      commit('DeactivateSearch');
      dispatch('search/setSearchParams', { terms: [] }, { root: true });
      dispatch('unselectItemsFrom', 'search');
    }

    if (displayResults !== undefined) commit('ToggleDisplaySearchView', displayResults);
  },
  addNavigationToHistory({ state, commit }, address) {
    // Do not add newCard navigations to history
    if (address.slice(address.length - 2) === '/0') return;

    let removeIndex = state.navigationHistory.findIndex(entry => entry === address);
    if (removeIndex < 0) removeIndex = null;
    commit('AddNavigationToHistory', { address, removeIndex });

    // For keyword- and starList, save the address (used for 'selected' indicator in sidebar)
    if (address.startsWith('/keyword/')) commit('SaveKeywordList', address);
    else if (address.startsWith('/stars/')) commit('SaveStarList', address);
  },
  setPinnedItems({ commit, dispatch, rootGetters }, items) {
    // Used at Byblio startup, when processing pinned cards (are stored in db by id and type)
    const pinnedItems = [];

    // Assume all are not pinned. If they are pinned, that will be fixed by the data from db.
    const currentOpenItems = state.openItems.map(i => ({ pinned: false, item: i.item }));

    items.forEach(({ id, type }) => {
      // This is necessary in case this action is called after polling the db, in which case existing openItems should be concatenated with pinnedItems from db
      const currentIndex = currentOpenItems.findIndex(i => i.item.id === id && i.item.type === type);
      if (currentIndex >= 0) currentOpenItems.splice(currentIndex, 1);

      let itemRef;
      if (type === 'card') itemRef = rootGetters['cards/getCardRef']({ cardId: id });
      else if (type === 'note') itemRef = rootGetters['notes/getNoteRef']({ noteId: id });

      if (!itemRef) {
        // It happens very rarely that an unpinnedCard was not updated in user settings. To be sure, here is a check (but the other issue should be fixed)
        // For now, unPin this card from userSettings and return.
        dispatch('users/unpinItem', { id, type }, { root: true });
        return;
      }

      const pinnedItem = {
        item: itemRef,
        pinned: true,
      };
      pinnedItems.push(pinnedItem);
    });

    // Sort pinned cards
    pinnedItems.sort((a, b) => {
      const titleA = a.item.title.toUpperCase();
      const titleB = b.item.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    commit('SetOpenItems', [...pinnedItems, ...currentOpenItems]);
  },
  togglePinOpenItem({ state, dispatch, commit, rootState }, item) {
    const itemIsPinned = !!state.openItems.find(entry => entry.pinned && entry.item.id === item.id && entry.item.type === item.type);
    let pinnedItems;
    let nonpinnedItems;
    let openItems;

    if (itemIsPinned) {
      // pinnedItems = state.openItems.filter(entry => entry.pinned && entry.item.id !== item.id && entry.item.type !== item.type);
      pinnedItems = state.openItems.filter(entry => entry.pinned);
      const toggleIndex = pinnedItems.findIndex(i => i.item.id === item.id && i.item.type === item.type);
      pinnedItems.splice(toggleIndex, 1);
      nonpinnedItems = state.openItems.filter(entry => !entry.pinned);
      nonpinnedItems.unshift({
        pinned: false,
        item,
      });

      // Merge arrays again and set them
      openItems = pinnedItems.concat(nonpinnedItems);
      commit('SetOpenItems', openItems);
      dispatch('users/unpinItem', { id: item.id, type: item.type }, { root: true });
      return;
    }

    // We should pin the item
    // Split openItems array
    pinnedItems = state.openItems.filter(entry => entry.pinned);
    nonpinnedItems = state.openItems.filter(entry => !entry.pinned);

    // Move relevant card to pinnedItems
    const index = nonpinnedItems.findIndex(entry => entry.item.id === item.id);
    const pinnedEntry = Object.assign(nonpinnedItems[index], { pinned: true });
    pinnedItems.unshift(pinnedEntry);
    nonpinnedItems.splice(index, 1);

    pinnedItems.sort((a, b) => {
      const titleA = a.item.title.toUpperCase();
      const titleB = b.item.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    // Merge arrays again and set them
    openItems = pinnedItems.concat(nonpinnedItems);
    commit('SetOpenItems', openItems);
    dispatch('users/pinItem', { id: item.id, type: item.type }, { root: true });
  },
  setPinnedCards({ commit, dispatch, rootGetters }, cardIds) {
    // Used at Byblio startup, when processing pinned cards (are stored in db by id)
    const pinnedCards = [];

    // Add all pinned cards to the array
    cardIds.forEach((cardId) => {
      const card = rootGetters['cards/getCardRef']({ cardId });
      if (!card) {
        // It happens very rarely that an unpinnedCard was not updated in user settings. To be sure, here is a check (but the other issue should be fixed)
        // For now, unPin this card from userSettings and return.
        dispatch('users/unpinCard', cardId, { root: true });
        return;
      }
      const openCard = {
        card,
        pinned: true,
      };
      pinnedCards.push(openCard);
    });

    // Sort pinned cards
    pinnedCards.sort((a, b) => {
      const titleA = a.card.title.toUpperCase();
      const titleB = b.card.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    commit('SetOpenCards', pinnedCards);
  },
  togglePinOpenCard({ state, dispatch, commit, rootState }, card) {
    const cardIsPinned = !!state.openCards.find(entry => entry.pinned && entry.card.id === card.id);
    let pinnedCards;
    let nonpinnedCards;
    let openCards;

    if (cardIsPinned) {
      pinnedCards = state.openCards.filter(entry => entry.pinned && entry.card.id !== card.id);
      nonpinnedCards = state.openCards.filter(entry => !entry.pinned);
      nonpinnedCards.unshift({
        pinned: false,
        card,
      });

      // Merge arrays again and set them
      openCards = pinnedCards.concat(nonpinnedCards);
      commit('SetOpenCards', openCards);
      commit('users/UnpinCard', card.id, { root: true });
      dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
      return;
    }

    // We should pin the card
    // Split openCards array
    pinnedCards = state.openCards.filter(entry => entry.pinned);
    nonpinnedCards = state.openCards.filter(entry => !entry.pinned);

    // Move relevant card to pinnedCards
    const index = nonpinnedCards.findIndex(entry => entry.card.id === card.id);
    const pinnedEntry = Object.assign(nonpinnedCards[index], { pinned: true });
    pinnedCards.unshift(pinnedEntry);
    nonpinnedCards.splice(index, 1);

    pinnedCards.sort((a, b) => {
      const titleA = a.card.title.toUpperCase();
      const titleB = b.card.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    // Merge arrays again and set them
    openCards = pinnedCards.concat(nonpinnedCards);
    commit('SetOpenCards', openCards);
    commit('users/PinCard', card.id, { root: true });
    dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
  },
  setPinnedNotes({ dispatch, commit, rootGetters }, noteIds) {
    // Used at Byblio startup, when processing pinned notes (are stored in db by id)
    const pinnedNotes = [];

    // Add all pinned notes to the array
    noteIds.forEach((noteId) => {
      const note = rootGetters['notes/getNoteRef']({ noteId });
      if (!note) {
        // See comment at setPinnedCards
        dispatch('users/unpinNote', noteId, { root: true });
        return;
      }

      const openNote = {
        note,
        pinned: true,
      };
      pinnedNotes.push(openNote);
    });

    // Sort pinned notes
    pinnedNotes.sort((a, b) => {
      const titleA = a.note.title.toUpperCase();
      const titleB = b.note.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    commit('SetOpenNotes', pinnedNotes);
  },
  togglePinOpenNote({ state, dispatch, commit, rootState }, note) {
    const noteIsPinned = !!state.openNotes.find(entry => entry.pinned && entry.note.id === note.id);
    let pinnedNotes;
    let nonpinnedNotes;
    let openNotes;

    if (noteIsPinned) {
      pinnedNotes = state.openNotes.filter(entry => entry.pinned && entry.note.id !== note.id);
      nonpinnedNotes = state.openNotes.filter(entry => !entry.pinned);
      nonpinnedNotes.unshift({
        pinned: false,
        note,
      });

      // Merge arrays again and set them
      openNotes = pinnedNotes.concat(nonpinnedNotes);
      commit('SetOpenNotes', openNotes);
      commit('users/UnpinNote', note.id, { root: true });
      dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
      return;
    }

    // We should pin the note
    // Split openNotes array
    pinnedNotes = state.openNotes.filter(entry => entry.pinned);
    nonpinnedNotes = state.openNotes.filter(entry => !entry.pinned);

    // Move relevant note to pinnedNotes
    const index = nonpinnedNotes.findIndex(entry => entry.note.id === note.id);
    const pinnedEntry = Object.assign(nonpinnedNotes[index], { pinned: true });
    pinnedNotes.push(pinnedEntry);
    nonpinnedNotes.splice(index, 1);

    pinnedNotes.sort((a, b) => {
      const titleA = a.note.title.toUpperCase();
      const titleB = b.note.title.toUpperCase();
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });

    // Merge arrays again and set them
    openNotes = pinnedNotes.concat(nonpinnedNotes);
    commit('SetOpenNotes', openNotes);
    commit('users/PinNote', note.id, { root: true });
    dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
  },
  toggleSelectedItem({ state, commit, rootState }, { id, itemId, itemType, forceRemove }) { // note that 'id' refers to collectionId or 'search'/'keyword'/'stars'
    if (!itemId && itemId !== 0) return;
    // to do: not sure if you can be sure that we're looking for 'search' is search.isActive... depends on how you deal with that
    if (!id) id = state.search.isActive ? 'search' : rootState.collections.selectedCollection; // eslint-disable-line no-param-reassign

    let index = state.selectedItems.findIndex(item => item.id === id);
    if (index < 0 && forceRemove) return; // If there is no entry for the relevant collection or for search, the item cannot be selected, so cancel here
    if (index < 0) {
      // If the collection or search entry can't be found, this means we're adding an item
      index = null;
      commit('AddToSelectedItems', { index, id, itemId, itemType });
      return;
    }

    // The collection or search entry is there, now check whether this item already was selected (which means it should be removed)
    const removeIndex = state.selectedItems[index].items.findIndex(selectedItem => selectedItem.id === itemId && selectedItem.type === itemType);

    if (removeIndex < 0 && !forceRemove) commit('AddToSelectedItems', { index, id, itemId, itemType });
    else if (removeIndex >= 0) commit('RemoveFromSelectedItems', { index, removeIndex }); // forceRemove always leads here, but we only need to remove if it's actually selected
  },
  unselectItemsFrom({ state, commit }, id) {
    const removeIndex = state.selectedItems.findIndex(item => item.id === id);
    if (removeIndex >= 0) commit('UnselectItemsByIndex', removeIndex);
  },
  selectItems({ state, commit }, { id, items }) {
    let index = state.selectedItems.findIndex(item => item.id === id);
    if (index < 0) index = null;

    commit('SelectItems', { index, id, items });
  },
  addSelectedItems({ state, commit }, { id, items: receivedItems }) {
    let items;
    let index = state.selectedItems.findIndex(item => item.id === id);
    if (index < 0) {
      index = null;
      items = receivedItems;
    }
    else items = state.selectedItems[index].items.concat(receivedItems);

    commit('SelectItems', { index, id, items });
  },
  showAddedItemInBackgroundIndicator({ commit }) {
    commit('ShowAddedItemInBackgroundIndicator');
    setTimeout(() => {
      commit('HideAddedItemInBackgroundIndicator');
    }, 300);
  },
  storeScrollPosition({ commit }, { type, id, scrollPosition }) {
    commit('StoreScrollPosition', { type, id, scrollPosition });
  },
};

const mutations = {
  StoreScrollPosition(state, { type, id, scrollPosition }) {
    state.scrollState[type][id] = scrollPosition;
  },
  ShowAddedItemInBackgroundIndicator(state) {
    state.displayAddedItemInBackgroundIndicator = true;
  },
  HideAddedItemInBackgroundIndicator(state) {
    state.displayAddedItemInBackgroundIndicator = false;
  },
  // Intro steps
  NextIntroStep(state) {
    state.introStep += 1;
  },
  PreviousIntroStep(state) {
    state.introStep -= 1;
  },
  StartIntro(state) {
    state.introStep = 1;
  },
  StopIntro(state) {
    state.introStep = 0;
  },
  // Open and pinned cards / notes
  AddOpenItem(state, item) {
    const { id, type } = item;

    const itemIsOpen = state.openItems.findIndex(entry => entry.item.id === id && entry.item.type === type) >= 0;
    if (itemIsOpen) return;

    const pinnedIndex = state.openItems.filter(entry => entry.pinned).length;
    state.openItems.splice(pinnedIndex, 0, {
      item,
      pinned: false,
    });
  },
  CloseOpenItem(state, item) {
    const { id, type } = item;

    const index = state.openItems.findIndex(entry => entry.item.id === id && entry.item.type === type);
    if (index >= 0) state.openItems.splice(index, 1);
  },
  UnpinItem(state, item) {
    const { id, type } = item;

    const index = state.openItems.findIndex(entry => entry.item.id === id && entry.item.type === type);
    if (index >= 0) Vue.set(state.openItems[index], 'pinned', false);
  },
  SetOpenItems(state, openItems) {
    Vue.set(state, 'openItems', openItems);
  },
  // old
  AddOpenCard(state, card) {
    const cardIsOpen = state.openCards.findIndex(entry => entry.card.id === card.id) >= 0;
    if (cardIsOpen) return;

    const pinnedIndex = state.openCards.filter(entry => entry.pinned).length;
    state.openCards.splice(pinnedIndex, 0, {
      card,
      pinned: false,
    });
  },
  CloseOpenCard(state, { id }) {
    const index = state.openCards.findIndex(entry => entry.card.id === id);
    if (index >= 0) state.openCards.splice(index, 1);
  },
  UnpinCard(state, card) {
    const index = state.openCards.findIndex(entry => entry.card.id === card.id);
    Vue.set(state.openCards[index], 'pinned', false);
  },
  ReorderOpenCard(state, { oldIndex, newIndex }) {
    state.openCards.splice(newIndex, 0, state.openCards.splice(oldIndex, 1)[0]);
  },
  SetOpenCards(state, openCards) {
    Vue.set(state, 'openCards', openCards);
  },
  AddOpenNote(state, note) {
    const noteIsOpen = state.openNotes.findIndex(entry => entry.note.id === note.id) >= 0;
    if (noteIsOpen) return;

    const pinnedIndex = state.openNotes.filter(entry => entry.pinned).length;
    state.openNotes.splice(pinnedIndex, 0, {
      note,
      pinned: false,
    });
  },
  CloseOpenNote(state, { id }) {
    const index = state.openNotes.findIndex(entry => entry.note.id === id);
    if (index >= 0) state.openNotes.splice(index, 1);
  },
  UnpinNote(state, note) {
    const index = state.openNotes.findIndex(entry => entry.note.id === note.id);
    Vue.set(state.openNotes[index], 'pinned', false);
  },
  ReorderOpenNote(state, { oldIndex, newIndex }) {
    state.openNotes.splice(newIndex, 0, state.openNotes.splice(oldIndex, 1)[0]);
  },
  SetOpenNotes(state, openNotes) {
    Vue.set(state, 'openNotes', openNotes);
  },
  // Selecting items (batch stuff)
  AddToSelectedItems(state, { index, id, itemId, itemType }) {
    if (index || index === 0) state.selectedItems[index].items.push({ id: itemId, type: itemType });
    else {
      // No index, so add new entry for selectId (collectionId or search/keyw/stars)
      const obj = Object.assign({
        items: [],
      }, { id });
      obj.items.push({ id: itemId, type: itemType });
      state.selectedItems.push(obj);
    }
  },
  RemoveFromSelectedItems(state, { index, removeIndex }) {
    state.selectedItems[index].items.splice(removeIndex, 1);
  },
  UnselectItemsByIndex(state, removeIndex) {
    state.selectedItems.splice(removeIndex, 1);
  },
  SelectItems(state, { index, id, items }) {
    if (index || index === 0) Vue.set(state.selectedItems[index], 'items', items);
    else {
      const obj = Object.assign({
        items: [],
      }, { id, items });
      state.selectedItems.push(obj);
    }
  },
  // Sidebar, selecting
  ExpandCollection(state, collectionId) {
    if (!state.expandedCollections.includes(collectionId)) state.expandedCollections.push(collectionId);
  },
  ToggleExpandCollection(state, collectionId) {
    const index = state.expandedCollections.findIndex(id => id === collectionId);
    if (index < 0) state.expandedCollections.push(collectionId);
    else state.expandedCollections.splice(index, 1);
  },
  SaveKeywordList(state, address) {
    state.lastKeywordList = address;
  },
  SaveStarList(state, address) {
    state.lastStarList = address;
  },
  // Expanding collections in collectionlist
  ToggleExpandCollectionInCollectionList(state, collectionId) {
    const index = state.expandedCollectionsInCollectionsList.findIndex(id => id === collectionId);
    if (index < 0) state.expandedCollectionsInCollectionsList.push(collectionId);
    else state.expandedCollectionsInCollectionsList.splice(index, 1);
  },
  ResetExpandedCollectionsInCollectionList(state) {
    state.expandedCollectionsInCollectionsList = [];
  },
  // State
  InitialDataLoaded(state) {
    state.initialDataLoaded = true;
  },
  SetScreenOverlayLogout(state) {
    state.screenOverlayLogout = true;
  },
  ToggleDisplayItemView(state, showItemView) {
    if (showItemView !== undefined) state.displayItemView = showItemView;
    else state.displayItemView = !state.displayItemView;
  },
  SetHighlightDragOverCollection(state, collectionId) {
    state.highlightDragOverCollection = collectionId;
  },
  SetMovingCollection(state, collectionId) {
    state.movingCollection = collectionId;
  },
  SetAppToReady(state) {
    state.appIsReady = true;
  },
  SetSidebarWidth(state, width) {
    state.localSettings.sidebarWidth = width;
    localStorage.setItem('sidebarWidth', width);
  },
  // History
  AddNavigationToHistory(state, { address, removeIndex }) {
    if (removeIndex) state.navigationHistory.splice(removeIndex, 1);
    state.navigationHistory.unshift(address);
    if (state.navigationHistory.length > 25) state.navigationHistory.pop();
  },
  DeleteFromHistory(state, payload) {
    const index = state.navigationHistory.findIndex(entry => entry === payload);
    if (index !== -1) state.navigationHistory.splice(index, 1);
  },
  // Other
  ResetState(state) {
    state.initialDataLoaded = false;
    state.fullscreenMode = false;
    state.navigationHistory = [];
  },
  // Search
  ActivateSearch(state) {
    state.search.isActive = true;
  },
  DeactivateSearch(state) {
    state.search.isActive = false;
  },
  ToggleDisplaySearchView(state, value) {
    if (value === undefined) state.search.displayResults = !state.search.displayResults;
    else state.search.displayResults = value;
  },
  // Sort
  SetCollectionViewSortSetting(state, setting) {
    state.localSettings.collectionViewSortSetting = setting;
    localStorage.setItem('collectionViewSortSetting', setting);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
